// Bank system data for SCD
// 3 banks with distinct advisors, personalities, rate formulas

export const BANKS = {
  bnb_mariba: {
    id: 'bnb_mariba',
    name: 'BNB Mariba',
    slogan: 'La banque qui vous accompagne depuis le premier kilomètre.',
    color: '#0D6E6E',
    accentColor: '#14B8A6',
    bgClass: 'bg-teal-50',
    borderClass: 'border-teal-500',
    advisor: {
      name: 'Clara Fontana',
      portrait: '/bankers/bnb_mariba.webp',
      title: 'Directrice d\'agence PME',
      personality: 'Chaleureuse et pragmatique. Clara a commencé comme stagiaire dans le transport. Elle comprend le métier et ses aléas.',
      style: 'empathique',
      greetings: [
        "Bonjour ! Ravie de vous revoir. Comment puis-je vous aider aujourd'hui ?",
        "Votre activité progresse bien. Parlons de vos projets de financement.",
        "Clara Fontana, BNB Mariba. Je suis là pour vous accompagner.",
      ],
      loanApproval: [
        "J'ai étudié votre dossier attentivement. Voici ce que je peux vous proposer.",
        "Votre profil est intéressant. On va trouver une solution ensemble.",
        "Compte tenu de votre activité, voici ma meilleure offre.",
      ],
      loanRefusal: [
        "Je suis désolée, mais votre niveau d'endettement ne nous permet pas d'aller plus loin pour le moment.",
        "Ce n'est pas un non définitif. Renforcez votre trésorerie et revenez me voir.",
      ],
      placementOffer: [
        "Avec votre trésorerie actuelle, je vous recommande notre Livret Pro. Un bon moyen de faire travailler votre argent.",
        "Vous avez accumulé un joli matelas. Et si on le faisait fructifier ?",
      ],
      loyaltyMessage: [
        "Votre fidélité me touche. En tant que cliente privilégiée, je vous débloque un taux préférentiel.",
        "Après {years} années de collaboration, vous méritez nos meilleures conditions.",
      ],
    },
    // Rate formula: BNB is the most favorable for small businesses, slightly higher for large amounts
    baseRate: 0.042,
    floorRate: 0.012, // 19/07/2026 — plancher propre à chaque banque (offres différenciées en fin de partie)
    rateModifiers: {
      reputationFactor: -0.0008, // Per reputation point above 50
      fleetFactor: -0.0003,      // Per vehicle
      loyaltyFactor: -0.002,     // Per loyalty level
      amountPenalty: 0.000001,   // Per euro above 50k
    },
    maxLoanRatio: 3.5,  // Max total debt = 3.5x monthly revenue
    loyaltyThresholds: [0, 2, 5, 10], // Loans repaid to reach next loyalty level
    specialties: ['PME transport', 'Première installation'],
    placementRate: 0.025, // 2.5% annual on savings
    placementMinTreasury: 15000,
    placementMinLoyalty: 2,
  },

  societe_generaliste: {
    id: 'societe_generaliste',
    name: 'Société Généraliste',
    slogan: 'La puissance d\'un grand groupe au service de votre ambition.',
    color: '#991B1B',
    accentColor: '#DC2626',
    bgClass: 'bg-red-50',
    borderClass: 'border-red-500',
    advisor: {
      name: 'Maxime Deschamps',
      portrait: '/bankers/societe_generaliste.webp',
      title: 'Chargé d\'affaires Entreprises',
      personality: 'Confiant et ambitieux. Maxime vise les gros dossiers. Il respecte les résultats, pas les excuses.',
      style: 'direct',
      greetings: [
        "Maxime Deschamps. Qu'est-ce que je peux faire pour votre croissance ?",
        "Bonjour. Je vois que votre CA progresse. On parle investissement ?",
        "Allons droit au but. Quel est votre projet ?",
      ],
      loanApproval: [
        "Votre dossier est solide. Voici nos conditions — elles sont compétitives, croyez-moi.",
        "J'ai obtenu une validation rapide de la direction. Voici l'offre.",
        "Avec votre profil, on peut aller vite. Voici les termes.",
      ],
      loanRefusal: [
        "Soyons francs : votre endettement est trop élevé. Réduisez votre exposition et revenez.",
        "Ce n'est pas personnel. Les ratios ne passent pas. Point.",
      ],
      placementOffer: [
        "Votre trésorerie dort. Chez nous, elle peut rapporter 3.2% sur un compte à terme.",
        "Vous avez les moyens de faire travailler votre cash. Parlons placement.",
      ],
      loyaltyMessage: [
        "Vous êtes un client fidèle. Je vous ai négocié un taux préférentiel auprès de la direction.",
        "La fidélité, ça se récompense. Voici votre nouveau barème privilégié.",
      ],
    },
    baseRate: 0.048,
    floorRate: 0.018, // 19/07/2026 — plancher propre à chaque banque
    rateModifiers: {
      reputationFactor: -0.0006,
      fleetFactor: -0.0005,  // Better discount for larger fleets
      loyaltyFactor: -0.003,
      amountPenalty: 0.0000005, // Lower penalty on large amounts
    },
    maxLoanRatio: 4.0,  // More generous for big companies
    loyaltyThresholds: [0, 3, 7, 15],
    specialties: ['Grandes flottes', 'Expansion nationale'],
    placementRate: 0.032, // 3.2% — best placement rate
    placementMinTreasury: 30000,
    placementMinLoyalty: 2,
  },

  credit_agricool: {
    id: 'credit_agricool',
    name: 'Crédit Agricool',
    slogan: 'Enracinés dans le territoire, tournés vers l\'avenir.',
    color: '#166534',
    accentColor: '#22C55E',
    bgClass: 'bg-green-50',
    borderClass: 'border-green-500',
    advisor: {
      name: 'Philippe Renard',
      portrait: '/bankers/credit_agricool.webp',
      title: 'Responsable Financement Pro',
      personality: 'Méthodique et bienveillant. Philippe analyse chaque dossier en profondeur. Il ne promet jamais ce qu\'il ne peut tenir.',
      style: 'analytique',
      greetings: [
        "Bonjour. J'ai préparé une analyse de votre situation. Regardons ça ensemble.",
        "Philippe Renard. Vos chiffres sont devant moi. Parlons de votre projet.",
        "Content de vous revoir. Votre dossier a évolué positivement.",
      ],
      loanApproval: [
        "Après analyse approfondie, voici la proposition la plus adaptée à votre profil.",
        "Les chiffres parlent d'eux-mêmes. Je peux vous proposer ceci.",
        "Mon analyse confirme la viabilité de votre projet. Voici les conditions.",
      ],
      loanRefusal: [
        "Mon analyse montre un ratio d'endettement trop élevé. Je vous recommande de stabiliser votre situation d'abord.",
        "Les indicateurs ne sont pas favorables aujourd'hui. Mais je garde votre dossier ouvert.",
      ],
      placementOffer: [
        "Vos excédents de trésorerie pourraient être optimisés. J'ai analysé plusieurs options pour vous.",
        "Un Livret Pro Territoire à 2.8%. C'est sécurisé et disponible. Mon analyse montre que c'est optimal pour vous.",
      ],
      loyaltyMessage: [
        "Notre collaboration porte ses fruits. J'ai le plaisir de vous accorder un taux préférentiel.",
        "Les données montrent une relation solide. Voici vos nouvelles conditions privilégiées.",
      ],
    },
    baseRate: 0.045,
    floorRate: 0.015, // 19/07/2026 — plancher propre à chaque banque
    rateModifiers: {
      reputationFactor: -0.0007,
      fleetFactor: -0.0004,
      loyaltyFactor: -0.0025,
      amountPenalty: 0.0000008,
    },
    maxLoanRatio: 3.8,
    loyaltyThresholds: [0, 2, 6, 12],
    specialties: ['Régional', 'Éco-responsable'],
    placementRate: 0.028,
    placementMinTreasury: 20000,
    placementMinLoyalty: 0,
  },
};

// Calculate the offered rate for a given bank + context
export const calculateOfferedRate = (bankId, { reputation, vehicleCount, monthlyRevenue, loyaltyLevel, loanAmount, creditScoreGrade }) => {
  const bank = BANKS[bankId];
  if (!bank) return 0.06;

  const mod = bank.rateModifiers;
  let rate = bank.baseRate;

  // Reputation bonus (above 50)
  rate += mod.reputationFactor * Math.max(0, (reputation || 50) - 50);

  // Fleet size bonus
  rate += mod.fleetFactor * (vehicleCount || 1);

  // Loyalty discount
  rate += mod.loyaltyFactor * (loyaltyLevel || 0);

  // Large amount penalty
  rate += mod.amountPenalty * Math.max(0, (loanAmount || 0) - 50000);

  // Revenue-based discount (larger company = slightly better rate)
  if (monthlyRevenue > 20000) rate -= 0.002;
  if (monthlyRevenue > 50000) rate -= 0.003;

  // Credit score bonus/penalty
  if (creditScoreGrade && CREDIT_SCORES[creditScoreGrade]) {
    rate += CREDIT_SCORES[creditScoreGrade].rateBonus;
  }

  // 19/07/2026 — user prod : « les 3 banques donnent exactement la même
  // réponse à 1,5 % ». Le plancher global 0.015 écrasait toute
  // différenciation en fin de partie (excellent score → les 3 banques
  // clampées). Chaque banque a désormais SON plancher (1,2 % / 1,5 % /
  // 1,8 %) → les offres restent comparables et distinctes.
  return Math.max(bank.floorRate || 0.015, Math.min(0.10, Math.round(rate * 10000) / 10000));
};

/**
 * Scale the revenue floor with the company age so a freshly-launched
 * carrier can't max-out 4 banks on day 1 (the previous flat 8 000 € floor
 * granted ~115 k € total day 1, which felt unrealistic for an outfit with
 * zero history). At J1 the floor is ~4 000 € → ~14-16 k € capacity per
 * bank (depending on `maxLoanRatio`), scaling linearly to the legacy
 * 8 000 € cap around J80. Once real monthly revenue exceeds the floor it
 * naturally takes over.
 *
 * Players should be incentivised to spread debt across multiple banks
 * (each bank applies the floor independently).
 */
export const getRevenueFloor = (totalDays = 0) => {
  // 04/02/2026 — Phase Q1 (retours user prod) : le floor d'âge a été
  // supprimé. La règle « emprunt impossible avant 90 jours » (MIN_COMPANY_AGE_FOR_LOAN)
  // rend ce floor obsolète : à J90+, le joueur a forcément un historique
  // de CA réel. Le maintenir conduisait à des prêts délirants (ex. 102 k€
  // alors que CA = 4 411 €/mois). Conservé en export pour compat tests.
  return 0;
};

/**
 * 04/02/2026 — Plafond absolu d'endettement (Phase Q1, retours user).
 * Toutes banques confondues, le total des prêts en cours après un nouvel
 * emprunt ne peut excéder MAX_DEBT_TO_REVENUE_RATIO × CA mensuel de
 * référence (max du CA en cours et de la moyenne des 4 derniers mois).
 *
 * Choix utilisateur : 6× CA mensuel. Cette valeur prévaut sur le
 * `bank.maxLoanRatio` historique : les banques restent différenciées
 * par leur taux / leur exigence de relation, mais le plafond
 * d'endettement est désormais commun.
 */
// 09/02/2026 — Q2 Lot H user prod : retour utilisateur « les prêts sont
// trop faciles ». On resserre le plafond d'endettement de 6× CA → 4× CA
// (plus aligné sur la pratique réelle des banques pour le transport PME,
// où le DSCR cible est ~1.3 et l'EBITDA-margin tourne autour de 8-10 %).
export const MAX_DEBT_TO_REVENUE_RATIO = 4;

// Check if a new loan is allowed based on financial health
export const MIN_COMPANY_AGE_FOR_LOAN = 90; // jours de jeu (≈ 3 mois)

export const canTakeLoan = (bankId, { totalDebt, monthlyRevenue, treasury, totalDays = 90, monthlyHistory = null }) => {
  const bank = BANKS[bankId];
  if (!bank) return { allowed: false, reason: 'Banque inconnue' };

  // 13/06/2026 — Jeune entreprise : aucun crédit avant 90 jours d'activité.
  // Retour utilisateur : « Au bout de 40 jours [...] je peux encore prendre
  // un gros crédit (80 000 €) ce qui rend le jeu assez simple. » Les
  // banquiers exigent désormais 3 mois de bilans intermédiaires.
  if (Number.isFinite(totalDays) && totalDays < MIN_COMPANY_AGE_FOR_LOAN) {
    return {
      allowed: false,
      tooEarly: true,
      unlockDay: MIN_COMPANY_AGE_FOR_LOAN,
      reason: `Votre société est trop jeune : les banques exigent au moins 3 mois d'historique d'exploitation (jour ${MIN_COMPANY_AGE_FOR_LOAN}) avant d'accorder un crédit professionnel.`,
    };
  }

  // 04/06/2026 — defensive: a legacy save can carry a NaN totalDebt that
  // poisons every downstream Math.min/Math.max. Treat non-finite inputs
  // as zero (the player's debt was unknown but at worst we let them borrow).
  const safeTotalDebt = Number.isFinite(totalDebt) ? totalDebt : 0;
  const safeMonthlyRevenue = Number.isFinite(monthlyRevenue) ? monthlyRevenue : 0;
  const safeTreasury = Number.isFinite(treasury) ? treasury : 0;

  // 25/06/2026 hotfix bug user — Les banques utilisent désormais la
  // **moyenne du CA des 4 derniers mois clos** lorsque disponible, pour
  // éviter l'effet « fin de mois vs début de mois » signalé : 70 K€
  // accordés le 30 d'un mois, 15 K€ deux jours plus tard sur le mois
  // suivant parce que `monthlyRevenue` était reset à 0. Une banque
  // sérieuse regarde l'historique trimestriel, pas la valeur instan-
  // tanée du mois en cours.
  let referenceMonthlyRevenue = safeMonthlyRevenue;
  if (Array.isArray(monthlyHistory) && monthlyHistory.length > 0) {
    const last4 = monthlyHistory.slice(-4);
    const sumRev = last4.reduce((s, m) => s + (m.totalRevenue || 0), 0);
    const avg = sumRev / last4.length;
    // Take the MAX of the live month-to-date and the trailing average :
    // ainsi un joueur en pleine montée en charge n'est pas pénalisé,
    // et un joueur fraîchement reset en début de mois bénéficie quand
    // même de son historique.
    referenceMonthlyRevenue = Math.max(safeMonthlyRevenue, avg);
  }

  // 04/02/2026 — Phase Q1 : plafond strict 6× CA (retour user prod).
  // Plus de floor d'âge ; on s'appuie uniquement sur le CA mensuel réel
  // (instantané OU moyenne 4 derniers mois clos).
  const revenueFloor = referenceMonthlyRevenue;
  const maxDebt = MAX_DEBT_TO_REVENUE_RATIO * revenueFloor;

  if (safeTotalDebt >= maxDebt) {
    const ratio = revenueFloor > 0 ? Math.round(safeTotalDebt / revenueFloor) : 999;
    return {
      allowed: false,
      reason: `Endettement maximum atteint (${ratio}× le CA mensuel, plafond ${MAX_DEBT_TO_REVENUE_RATIO}×)`,
      maxDebt,
    };
  }

  if (safeTreasury < 500) {
    return { allowed: false, reason: 'Trésorerie insuffisante (min. 500 €)' };
  }

  const remainingCapacity = maxDebt - safeTotalDebt;
  return { allowed: true, maxAmount: Math.round(remainingCapacity), maxDebt };
};

// Get loyalty level from number of repaid loans + optional seniority bonus.
// Seniority bonus (in years of relationship): +1 level after 12 months,
// +1 additional level after 24 months — capped at the highest tier (3).
export const getLoyaltyLevel = (bankId, loansRepaid, seniorityMonths = 0) => {
  const bank = BANKS[bankId];
  if (!bank) return 0;
  const thresholds = bank.loyaltyThresholds;
  let level = 0;
  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (loansRepaid >= thresholds[i]) { level = i; break; }
  }
  // Seniority bonus.
  if (seniorityMonths >= 24) level = Math.min(thresholds.length - 1, level + 2);
  else if (seniorityMonths >= 12) level = Math.min(thresholds.length - 1, level + 1);
  return level;
};

// Helper: compute seniority (in months) for a bank relation.
// Returns null when the player has never interacted with the bank.
export const getBankSeniorityMonths = (rel, totalDays = 0) => {
  if (!rel || rel.firstLoanDay == null) return null;
  return Math.max(0, Math.floor((totalDays - rel.firstLoanDay) / 30));
};

export const LOYALTY_LABELS = ['Nouveau client', 'Client régulier', 'Client fidèle', 'Client privilégié'];

// Localized accessor — falls back to FR when EN unavailable
export const getLoyaltyLabel = (idx) => {
  try {
    // Lazy import to avoid circular dep
    const useLanguageStore = require('../i18n').useLanguageStore;
    const banksEN = require('./translations/banks_en.json');
    if (useLanguageStore.getState().language === 'en' && banksEN.LOYALTY_LABELS && banksEN.LOYALTY_LABELS[idx]) {
      return banksEN.LOYALTY_LABELS[idx];
    }
  } catch { /* fall back to FR labels */ }
  return LOYALTY_LABELS[idx];
};

// ===== SCORING BANCAIRE =====
export const CREDIT_SCORES = {
  A: { label: 'A — Excellent', color: '#16A34A', rateBonus: -0.008, maxLoanBonus: 1.3, description: 'Entreprise très solide. Accès aux meilleures conditions.' },
  B: { label: 'B — Bon', color: '#2563EB', rateBonus: -0.003, maxLoanBonus: 1.1, description: 'Bonne santé financière. Conditions favorables.' },
  C: { label: 'C — Moyen', color: '#D97706', rateBonus: 0, maxLoanBonus: 1.0, description: 'Situation correcte. Conditions standard.' },
  D: { label: 'D — Fragile', color: '#DC2626', rateBonus: 0.006, maxLoanBonus: 0.8, description: 'Situation tendue. Conditions restrictives.' },
};

// Calculate credit score from financial metrics
// 09/02/2026 — Q2 Lot H user prod : retour « les prêts sont trop faciles ».
// On resserre TOUS les axes de notation pour que le grade A devienne un
// vrai accomplissement (au lieu d'être quasi-systématique à 6 mois) :
//   - Base abaissée 50 → 40
//   - Réputation : palier -40 au lieu de -30 (un rep neutre 50 = +5 seulement)
//   - Trésorerie : seuils relevés (50k+ → +12 au lieu de +15)
//   - Debt ratio : courbe plus sévère (debt > 3× CA pénalisé)
//   - Flotte : poids réduit (max +6 au lieu de +10)
//   - Expérience : palier 1 pt / 2 mois (max +10) au lieu de 1 pt / mois
//   - Profitability : seuil de marge plus strict
//   - Grade : 80+ pour A, 60+ pour B, 40+ pour C, sinon D
export const calculateCreditScore = ({ reputation, treasury, monthlyRevenue, totalDebt, vehicleCount, driversCount, totalDays, loansRepaidTotal, premiumInsuranceRatio = 0 }) => {
  let score = 40; // Base score on 100 (resserré)

  // Reputation (max +20) — pondération réduite : reputation 50 → +5 seulement
  score += Math.min(20, Math.max(-10, ((reputation || 50) - 40) * 0.4));

  // Treasury health (max +12) — seuils relevés
  if (treasury > 80000) score += 12;
  else if (treasury > 40000) score += 8;
  else if (treasury > 15000) score += 4;
  else if (treasury < 2000) score -= 12;

  // Revenue vs Debt ratio (max +12) — courbe plus sévère
  const debtRatio = totalDebt / Math.max(monthlyRevenue, 1000);
  if (debtRatio < 0.5) score += 12;
  else if (debtRatio < 1.5) score += 6;
  else if (debtRatio < 2.5) score += 2;
  else if (debtRatio > 3) score -= 8;
  if (debtRatio > 5) score -= 8; // double-malus si surendetté

  // Fleet size (max +6) — réduit, le banquier valorise moins la flotte brute
  score += Math.min(6, (vehicleCount || 0) * 1.2);

  // Workforce (max +4)
  score += Math.min(4, (driversCount || 0) * 1);

  // Experience / longevity (max +10) — 1 pt tous les 2 mois
  const months = Math.floor((totalDays || 0) / 30);
  score += Math.min(10, months * 0.5);

  // Loan repayment history (max +10) — 3 prêts remboursés = +9
  score += Math.min(10, (loansRepaidTotal || 0) * 3);

  // Profitability bonus — seuil de marge plus strict (40 % CA > dette mensuelle)
  if (monthlyRevenue > 0 && monthlyRevenue > totalDebt * 0.4) score += 4;

  // Premium-insurance bonus: a fleet fully insured Premium signals reliability
  // to the banks. Scales linearly with the ratio (max +5 when 100% Premium).
  score += Math.round(Math.min(1, Math.max(0, premiumInsuranceRatio)) * 5);

  // Clamp 0-100
  score = Math.max(0, Math.min(100, Math.round(score)));

  // Map to grade — seuils relevés (A exigeant)
  let grade;
  if (score >= 80) grade = 'A';
  else if (score >= 60) grade = 'B';
  else if (score >= 40) grade = 'C';
  else grade = 'D';

  return { score, grade, ...CREDIT_SCORES[grade] };
};

// Advisor messages when revealing the score
export const SCORE_REVEAL_MESSAGES = {
  bnb_mariba: {
    A: "Après analyse de vos trois premiers mois, je suis ravie de vous annoncer que votre note de crédit est excellente ! Vous avez accès à nos meilleures conditions. Bravo !",
    B: "Vos trois premiers mois sont encourageants. Votre note de crédit est bonne. Continuez sur cette lancée et les meilleures conditions suivront.",
    C: "J'ai étudié vos trois premiers mois. Votre situation est correcte, mais il y a des axes d'amélioration. On va travailler ensemble pour progresser.",
    D: "Soyons transparents : vos trois premiers mois montrent des fragilités. Mais ne vous découragez pas — je suis là pour vous aider à remonter la pente.",
  },
  societe_generaliste: {
    A: "J'ai les résultats de votre scoring. Note A — excellent. Avec ces chiffres, on peut parler de vrais projets de croissance.",
    B: "Scoring terminé : note B. C'est solide. Quelques ajustements et vous décrocherez le A. Je compte sur vous.",
    C: "Les chiffres sont sur mon bureau. Note C — moyen. Il faut serrer les boulons si vous voulez accéder à nos meilleures offres.",
    D: "Je vais être direct : note D. Les ratios ne sont pas bons. Redressez la barre avant de revenir me voir pour un financement.",
  },
  credit_agricool: {
    A: "Mon analyse trimestrielle est terminée. Votre score de crédit est A — excellent. Les données confirment une gestion exemplaire. Félicitations.",
    B: "Les indicateurs des trois premiers mois donnent un score B. C'est un bon résultat. Mon analyse suggère quelques optimisations possibles.",
    C: "Score C après analyse. La situation est stable mais perfectible. Je vous recommande de consolider votre trésorerie avant tout nouvel emprunt.",
    D: "Mon analyse révèle un score D. Les chiffres ne mentent pas. Concentrez-vous sur la réduction de vos charges et la génération de revenus.",
  },
};

// Placement products catalog per bank
export const PLACEMENT_PRODUCTS = {
  bnb_mariba: [
    { id: 'bnb-livret', name: 'Livret Pro BNB', rate: 0.025, minAmount: 3000, termMonths: 0, liquid: true, minTreasury: 10000, minLoyalty: 0, description: 'Épargne disponible à tout moment. Idéal pour vos réserves de trésorerie.', icon: 'piggy' },
    { id: 'bnb-cat-6', name: 'Compte à Terme 6 mois', rate: 0.032, minAmount: 8000, termMonths: 6, liquid: false, minTreasury: 20000, minLoyalty: 1, description: 'Rendement garanti sur 6 mois. Capital bloqué.', icon: 'lock' },
    { id: 'bnb-fleet', name: 'Plan Épargne Flotte', rate: 0.015, minAmount: 5000, termMonths: 0, liquid: true, minTreasury: 15000, minLoyalty: 0, description: 'Épargne dédiée à l\'achat de véhicules. Avantage fiscal : IS réduit de 3%.', icon: 'truck', taxBonus: 0.03 },
  ],
  societe_generaliste: [
    { id: 'sg-livret', name: 'Livret Entreprise SG', rate: 0.022, minAmount: 5000, termMonths: 0, liquid: true, minTreasury: 15000, minLoyalty: 0, description: 'Épargne liquide avec un rendement compétitif.', icon: 'piggy' },
    { id: 'sg-cat-12', name: 'Compte à Terme 12 mois', rate: 0.038, minAmount: 15000, termMonths: 12, liquid: false, minTreasury: 30000, minLoyalty: 1, description: 'Notre produit phare. Le meilleur rendement garanti du marché.', icon: 'lock' },
    { id: 'sg-av', name: 'Assurance-Vie Entreprise', rate: 0.045, minAmount: 20000, termMonths: 24, liquid: false, minTreasury: 50000, minLoyalty: 3, description: 'Placement long terme à 4.5%. Réservé aux clients privilégiés.', icon: 'shield' },
  ],
  credit_agricool: [
    { id: 'ca-livret', name: 'Livret Pro Territoire', rate: 0.028, minAmount: 3000, termMonths: 0, liquid: true, minTreasury: 8000, minLoyalty: 0, description: 'Épargne disponible avec un taux attractif. Notre best-seller.', icon: 'piggy' },
    { id: 'ca-cat-6', name: 'Dépôt à Terme Vert 6 mois', rate: 0.034, minAmount: 5000, termMonths: 6, liquid: false, minTreasury: 15000, minLoyalty: 0, description: 'Placement éco-responsable. Vos fonds financent la transition verte.', icon: 'leaf' },
    { id: 'ca-fleet', name: 'Plan Épargne Flotte', rate: 0.018, minAmount: 5000, termMonths: 0, liquid: true, minTreasury: 12000, minLoyalty: 0, description: 'Épargnez pour votre prochaine acquisition. Avantage fiscal IS -3%.', icon: 'truck', taxBonus: 0.03 },
    { id: 'ca-cat-12', name: 'Placement Terroir 12 mois', rate: 0.040, minAmount: 10000, termMonths: 12, liquid: false, minTreasury: 25000, minLoyalty: 2, description: 'Rendement supérieur pour les clients fidèles. Capital garanti.', icon: 'lock' },
  ],
};

// Get available placements for a bank based on player context
export const getAvailablePlacements = (bankId, { treasury, loyaltyLevel }) => {
  const products = PLACEMENT_PRODUCTS[bankId];
  if (!products) return [];
  return products.map(p => ({
    ...p,
    bankId,
    maxAmount: Math.round(Math.min(treasury * 0.6, 500000)),
    unlocked: treasury >= p.minTreasury && loyaltyLevel >= p.minLoyalty,
    lockReason: treasury < p.minTreasury
      ? `Trésorerie min. ${p.minTreasury.toLocaleString('fr-FR')} €`
      : loyaltyLevel < p.minLoyalty
        ? `Fidélité min. : ${LOYALTY_LABELS[p.minLoyalty]}`
        : null,
  }));
};
